import { Injectable, inject } from '@angular/core';
import { Observable, map, of, switchMap, throwError } from 'rxjs';
import { HttpService } from 'src/app/http/http.service';
import { CommonService } from 'src/app/services/common.service';
import { ConstantUtil } from 'src/app/util/constant.util';

export type CustomerLabelOption = {
  id: string;
  name: string;
  contactCount?: number;
};

export type CustomerLabelListResult = {
  totalCount: number;
  count: number;
  hasNextPage: boolean;
  pageIndex: number;
  refreshTime: number;
  items: CustomerLabelOption[];
};

export type LeadReasonOption = {
  id: string;
  name: string;
  sourceType: string;
  description: string;
  bizId: string;
  validMode: 'permanent' | 'dated';
  validUntil: string | null;
  count: number;
};

export type InboundReasonListResult = {
  items: LeadReasonOption[];
  total: number;
};

export type PersonalBroadcastPayload = {
  tenantId: string;
  serviceNumberId?: string;
  name?: string;
  title?: string;
  remark?: string;
  channel?: string;
  labelIds: string[];
  matchLogic: MatchLogic;
  sendMode: 'immediate' | 'scheduled';
  sendAt: string | null;
  messages: Array<
    | { type: 'text'; content: string }
    | { type: 'image'; name: string; size: number; fileId: string; url?: string; thumbnailUrl?: string }
    | { type: 'file'; name: string; size: number; mimeType: string; fileId: string; url?: string }
    | {
        type: 'template';
        title: string;
        subtitle: string;
        imageUrl: string;
        buttons: Array<
          | { text: string; action: 'link'; url: string }
          | { text: string; action: 'leadReason'; leadReasonId: string; leadReasonName: string }
        >;
      }
  >;
};

export enum MatchLogic {
  And = 'AND',
  Or = 'OR'
}

export type BroadcastUploadResult = {
  fileId: string;
  url?: string;
  thumbnailUrl?: string;
};

export type BroadcastLabelInfo = {
  id: string;
  name: string;
};

export type BroadcastTaskListItem = {
  id: string;
  title: string;
  remark: string;
  status: string;
  broadcastTime: number | null;
  channel: string;
  labelInfos: BroadcastLabelInfo[];
  expectedCount: number;
  successCount: number;
  failedCount: number;
};

export type BroadcastTaskListResult = {
  items: BroadcastTaskListItem[];
  totalCount: number;
};

export type BroadcastTaskDetail = {
  id: string;
  title: string;
  remark: string;
  channel: string;
  labelIds: string[];
  matchLogic: MatchLogic;
  broadcastTime: number | null;
  content: Array<{ index?: number; type?: string; content?: any }>;
};

export type BroadcastDeliveryResult = {
  id: string;
  customerId: string;
  customerName?: string;
  roomId?: string;
  scopeId?: string;
  channel?: string;
  status: string;
  errorCode?: string;
  errorMessage?: string;
  finishTime?: number;
};

export type BroadcastDeliveryListResult = {
  items: BroadcastDeliveryResult[];
  totalCount: number;
};

@Injectable({ providedIn: 'root' })
export class PersonalBroadcastService {
  private readonly http = inject(HttpService);
  private readonly commonService = inject(CommonService);

  private resolveServiceNumberId(tenantId: string, serviceNumberId?: string): string {
    return String(serviceNumberId || '').trim() || this.commonService.getCurrentTenantBossSnid() || tenantId;
  }

  listCustomerLabels(params: {
    tenantId: string;
    serviceNumberId?: string;
    pageIndex: number;
    pageSize: number;
    refreshTime: number;
    key?: string;
  }): Observable<CustomerLabelListResult> {
    const payload = {
      pageIndex: params.pageIndex,
      pageSize: params.pageSize,
      direction: 'desc',
      refreshTime: params.refreshTime,
      serviceNumberId: this.resolveServiceNumberId(params.tenantId, params.serviceNumberId),
      type: 'ServiceNumber',
      onlyWithContacts: true,
      key: params.key || undefined
    };
    return this.http.post(ConstantUtil.TENANT_LABEL_V1_LIST, payload).pipe(
      map((res: any) => {
        if (!res?.success) {
          return { totalCount: 0, count: 0, hasNextPage: false, pageIndex: params.pageIndex, refreshTime: params.refreshTime, items: [] };
        }
        const data = res?.data || {};
        const items = (data?.items || []) as any[];
        const hasContactCountField = items.some(raw => raw?.contactCount !== undefined && raw?.contactCount !== null);
        const mapped = items
          .map(raw => {
            const parsedContactCount = Number(raw?.contactCount);
            return {
              id: String(raw?.id || '').trim(),
              name: String(raw?.name || '').trim(),
              contactCount: Number.isFinite(parsedContactCount) ? parsedContactCount : undefined
            };
          })
          .filter(item => item.id && item.name && (!hasContactCountField || Number(item.contactCount || 0) > 0));
        const unique = new Map<string, CustomerLabelOption>();
        mapped.forEach(item => unique.set(item.id, item));
        return {
          totalCount: Number(data?.totalCount || 0),
          count: Number(data?.count || 0),
          hasNextPage: Boolean(data?.hasNextPage),
          pageIndex: Number(data?.pageIndex ?? params.pageIndex),
          refreshTime: Number(data?.refreshTime ?? params.refreshTime),
          items: Array.from(unique.values())
        };
      })
    );
  }

  listInboundReasons(params: {
    tenantId: string;
    pageIndex: number;
    pageSize: number;
    key?: string;
    sourceType?: string;
    status?: 'Enable' | 'Disable';
  }): Observable<InboundReasonListResult> {
    const payload: any = {
      tenantId: params.tenantId,
      pageIndex: params.pageIndex,
      pageSize: params.pageSize,
      direction: 'desc',
      key: params.key || undefined,
      sourceType: params.sourceType || undefined,
      status: params.status || undefined
    };
    return this.http.post(ConstantUtil.TENANT_INBOUND_REASON_V1_LIST, payload).pipe(
      map((res: any) => {
        if (!res?.success) return { items: [], total: 0 };
        const items = (res?.data?.items || []).map((raw: any) => this.mapInboundReasonItem(raw));
        const total = Number(res?.data?.totalCount || 0);
        return { items, total };
      })
    );
  }

  getInboundReasonItem(tenantId: string, id: string): Observable<LeadReasonOption | null> {
    const resolvedId = String(id || '').trim();
    if (!resolvedId) return of(null);
    return this.http.post(`${ConstantUtil.TENANT_INBOUND_REASON_V1_ITEM}/${encodeURIComponent(resolvedId)}`, {}).pipe(
      map((res: any) => {
        if (!res?.success || !res?.data) return null;
        return this.mapInboundReasonItem(res.data);
      })
    );
  }

  saveInboundReason(params: {
    tenantId: string;
    sourceName: string;
    sourceType: string;
    description?: string;
    businessId?: string;
    validityPeriod: number;
  }): Observable<boolean> {
    const payload = {
      tenantId: params.tenantId,
      sourceName: params.sourceName,
      sourceType: params.sourceType,
      description: params.description,
      businessId: params.businessId,
      validityPeriod: params.validityPeriod,
      status: true
    };
    return this.http.post(ConstantUtil.TENANT_INBOUND_REASON_V1_SAVE, payload).pipe(map((res: any) => Boolean(res?.success)));
  }

  listLeadReasons(tenantId: string): Observable<LeadReasonOption[]> {
    return this.listInboundReasons({ tenantId, pageIndex: 0, pageSize: 20, status: 'Enable' }).pipe(map(res => res.items));
  }

  createLeadReason(tenantId: string, data: Omit<LeadReasonOption, 'id' | 'count'>): Observable<LeadReasonOption> {
    const validityPeriod = data.validMode === 'dated' && data.validUntil ? new Date(data.validUntil).getTime() : 0;
    return this.saveInboundReason({
      tenantId,
      sourceName: data.name,
      sourceType: data.sourceType,
      description: data.description,
      businessId: data.bizId,
      validityPeriod
    }).pipe(
      switchMap(success => {
        if (!success) return of({ ...data, id: '', count: 0 });
        return this.listInboundReasons({ tenantId, pageIndex: 0, pageSize: 1, key: data.name, status: 'Enable' }).pipe(
          map(res => res.items[0] || { ...data, id: '', count: 0 })
        );
      })
    );
  }

  estimateRecipients(_tenantId: string, _labelIds: string[], _matchMode: 'or' | 'and', serviceNumberId?: string): Observable<number> {
    const tenantId = String(_tenantId || '').trim();
    const labelIds = (_labelIds || []).map(id => String(id || '').trim()).filter(Boolean);
    const matchMode = (_matchMode || 'or') as 'or' | 'and';
    if (!tenantId || labelIds.length === 0) return of(0);
    const payload = {
      labelIds,
      matchLogic: matchMode === 'and' ? MatchLogic.And : MatchLogic.Or,
      serviceNumberId: this.resolveServiceNumberId(tenantId, serviceNumberId)
    };
    return this.http.post(ConstantUtil.TENANT_LABEL_OBJECT_V1_CONTACT_COUNT_BY_LABELS, payload).pipe(
      map((res: any) => {
        if (!res?.success) return 0;
        return Number(res?.data || 0);
      })
    );
  }

  uploadBroadcastAttachment(messageType: 'File' | 'Image', file: File): Observable<BroadcastUploadResult> {
    return this.http.postFormData(ConstantUtil.BASE_ATTACHMENT_V1_BROADCAST_UPLOAD, { messageType }, file).pipe(
      switchMap((res: any) => {
        if (!res?.success) {
          return throwError(() => new Error(String(res?.msg || '上传失败')));
        }
        const data = res?.data;
        const fileId = String(data?.fileId || data?.id || data?.file_id || data || '').trim();
        if (!fileId) {
          return throwError(() => new Error('上传失败'));
        }
        const url = String(data?.url || data?.fileUrl || data?.downloadUrl || '').trim() || undefined;
        const thumbnailUrl = String(data?.thumbnailUrl || data?.thumbUrl || '').trim() || undefined;
        return of({ fileId, url, thumbnailUrl });
      })
    );
  }

  listBroadcastTasks(params: {
    tenantId: string;
    serviceNumberId?: string;
    pageIndex: number;
    pageSize: number;
    key?: string;
  }): Observable<BroadcastTaskListResult> {
    const payload: any = {
      serviceNumberId: this.resolveServiceNumberId(params.tenantId, params.serviceNumberId),
      pageIndex: params.pageIndex,
      pageSize: params.pageSize,
      direction: 'desc',
      key: params.key || undefined
    };
    return this.http.post(ConstantUtil.TENANT_SERVICENUMBER_BROADCAST_V1_LIST, payload).pipe(
      map((res: any) => {
        if (!res?.success) return { items: [], totalCount: 0 };
        const data = res?.data || {};
        const rawItems = (data?.items || data?.list || []) as any[];
        const mapped = rawItems
          .map((raw: any) => this.mapBroadcastTaskListItem(raw))
          .filter((item: BroadcastTaskListItem) => Boolean(item.id));
        return {
          items: mapped,
          totalCount: Number(data?.totalCount ?? data?.total ?? mapped.length ?? 0)
        };
      })
    );
  }

  getBroadcastTaskDetail(tenantId: string, taskId: string, serviceNumberId?: string): Observable<BroadcastTaskDetail | null> {
    const resolvedId = String(taskId || '').trim();
    if (!resolvedId) return of(null);
    const payload: any = { serviceNumberId: this.resolveServiceNumberId(tenantId, serviceNumberId) };
    return this.http.post(`${ConstantUtil.TENANT_SERVICENUMBER_BROADCAST_V1_ITEM}/${encodeURIComponent(resolvedId)}`, payload).pipe(
      map((res: any) => {
        if (!res?.success || !res?.data) return null;
        return this.mapBroadcastTaskDetail(res.data, resolvedId);
      })
    );
  }

  listBroadcastDeliveryResults(params: {
    broadcastId: string;
    pageIndex: number;
    pageSize: number;
    status?: string;
  }): Observable<BroadcastDeliveryListResult> {
    return this.http
      .post(ConstantUtil.TENANT_SERVICENUMBER_BROADCAST_DELIVERY_V1_LIST, {
        broadcastId: String(params.broadcastId || '').trim(),
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
        status: params.status || undefined
      })
      .pipe(
        map((res: any) => {
          if (!res?.success) return { items: [], totalCount: 0 };
          const data = res?.data || {};
          return {
            items: (data?.items || []).map((raw: any) => ({
              id: String(raw?.id || ''),
              customerId: String(raw?.customerId || ''),
              customerName: raw?.customerName ? String(raw.customerName) : undefined,
              roomId: raw?.roomId ? String(raw.roomId) : undefined,
              scopeId: raw?.scopeId ? String(raw.scopeId) : undefined,
              channel: raw?.channel ? String(raw.channel) : undefined,
              status: String(raw?.status || 'Pending'),
              errorCode: raw?.errorCode ? String(raw.errorCode) : undefined,
              errorMessage: raw?.errorMessage ? String(raw.errorMessage) : undefined,
              finishTime: raw?.finishTime == null ? undefined : Number(raw.finishTime)
            })),
            totalCount: Number(data?.totalCount ?? data?.total ?? 0)
          };
        })
      );
  }

  deleteBroadcastTask(taskId: string): Observable<void> {
    const resolvedId = String(taskId || '').trim();
    if (!resolvedId) return throwError(() => new Error('缺少任务 ID'));
    return this.http.post(`${ConstantUtil.TENANT_SERVICENUMBER_BROADCAST_V1_DELETE}/${encodeURIComponent(resolvedId)}`, {}).pipe(
      switchMap((res: any) => {
        if (!res?.success) return throwError(() => new Error(String(res?.msg || '删除失败')));
        return of(void 0);
      })
    );
  }

  updateBroadcast(taskId: string, payload: PersonalBroadcastPayload): Observable<void> {
    const resolvedId = String(taskId || '').trim();
    if (!resolvedId) return throwError(() => new Error('缺少任务 ID'));
    const serviceNumberId = this.resolveServiceNumberId(payload.tenantId, payload.serviceNumberId);
    const broadcastTime = payload.sendMode === 'scheduled' && payload.sendAt ? new Date(payload.sendAt).getTime() : 0;
    const content = this.buildBroadcastContent(payload);
    const requestBody: any = {
      id: resolvedId,
      serviceNumberId,
      broadcastTime,
      labelIds: payload.labelIds,
      matchLogic: payload.matchLogic,
      content
    };
    const name = String(payload.name || payload.title || '').trim();
    if (name) requestBody.name = name;
    if (payload.remark) requestBody.remark = payload.remark;
    if (payload.channel && payload.channel !== 'Preset') {
      requestBody.channel = payload.channel;
    }
    return this.http.post(ConstantUtil.TENANT_SERVICENUMBER_BROADCAST_V1_UPDATE, requestBody).pipe(
      switchMap((res: any) => {
        if (!res?.success) return throwError(() => new Error(String(res?.msg || '保存失败')));
        return of(void 0);
      })
    );
  }

  createOrSendBroadcast(payload: PersonalBroadcastPayload): Observable<void> {
    const serviceNumberId = this.resolveServiceNumberId(payload.tenantId, payload.serviceNumberId);
    const broadcastTime = payload.sendMode === 'scheduled' && payload.sendAt ? new Date(payload.sendAt).getTime() : 0;
    const content = this.buildBroadcastContent(payload);

    const requestBody: any = {
      serviceNumberId,
      broadcastTime,
      labelIds: payload.labelIds,
      matchLogic: payload.matchLogic,
      content
    };
    const name = String(payload.name || payload.title || '').trim();
    if (name) requestBody.name = name;
    if (payload.remark) requestBody.remark = payload.remark;
    if (payload.channel && payload.channel !== 'Preset') {
      requestBody.channel = payload.channel;
    }

    return this.http.post(ConstantUtil.TENANT_SERVICENUMBER_BROADCAST_V1_CREATE, requestBody).pipe(
      switchMap((res: any) => {
        if (!res?.success) return throwError(() => new Error(String(res?.msg || '保存失败')));
        return of(void 0);
      })
    );
  }

  private buildBroadcastContent(payload: PersonalBroadcastPayload): Array<{ index: number; type: string; content: string }> {
    return payload.messages.map((msg, idx) => {
      const index = idx + 1;
      if (msg.type === 'text') {
        return { index, type: 'Text', content: JSON.stringify({ text: msg.content }) };
      }
      if (msg.type === 'image') {
        const imageContent: any = {
          type: 'image',
          fileId: msg.fileId,
          name: msg.name,
          size: msg.size
        };
        if (msg.url) imageContent.url = msg.url;
        if (msg.thumbnailUrl) imageContent.thumbnailUrl = msg.thumbnailUrl;
        return { index, type: 'Image', content: JSON.stringify(imageContent) };
      }
      if (msg.type === 'file') {
        const fileContent: any = {
          type: 'file',
          fileId: msg.fileId,
          name: msg.name,
          size: msg.size,
          mimeType: (msg as any).mimeType || undefined
        };
        if (msg.url) fileContent.url = msg.url;
        return { index, type: 'File', content: JSON.stringify(fileContent) };
      }
      const actions = msg.buttons.map(btn => {
        if (btn.action === 'link') {
          return { type: 'Url', label: btn.text, url: btn.url };
        }
        return {
          type: 'Postback',
          label: btn.text,
          displayText: btn.text,
          data: JSON.stringify({ event: 'InboundReason', sourceId: btn.leadReasonId })
        };
      });
      return {
        index,
        type: 'Template',
        content: JSON.stringify({
          type: 'Carousel',
          title: msg.title,
          subTitle: msg.subtitle,
          text: '',
          imageUrl: msg.imageUrl,
          actions
        })
      };
    });
  }

  private mapBroadcastTaskListItem(raw: any): BroadcastTaskListItem {
    const id = String(raw?.id ?? raw?.taskId ?? raw?.broadcastId ?? '').trim();
    const title = String(raw?.name ?? raw?.title ?? raw?.subject ?? '').trim();
    const remark = String(raw?.remark ?? raw?.description ?? '').trim();
    const status = String(raw?.statusLabel ?? raw?.status ?? raw?.state ?? '').trim();
    const normalizeEpochMs = (value: any): number | null => {
      if (value == null) return null;
      const num = Number(value);
      if (!Number.isFinite(num)) return null;
      if (num <= 0) return num;
      return num < 1_000_000_000_000 ? num * 1000 : num;
    };
    const broadcastTimeMs = normalizeEpochMs(raw?.broadcastTime ?? raw?.sendTime ?? raw?.time ?? null);
    const createdAtMs = normalizeEpochMs(raw?.createdAt ?? raw?.createTime ?? raw?.createdTime ?? raw?.createAt ?? null);
    const resolvedBroadcastTime =
      broadcastTimeMs != null && broadcastTimeMs > 0 ? broadcastTimeMs : broadcastTimeMs === 0 ? createdAtMs : createdAtMs;
    const channel = String(raw?.channel ?? raw?.platform ?? raw?.channelType ?? 'Preset').trim() || 'Preset';
    const labelInfosRaw = raw?.labelInfos ?? raw?.labelInfoList ?? raw?.labelInfo ?? null;
    let labelInfos: BroadcastLabelInfo[] = [];
    if (Array.isArray(labelInfosRaw)) {
      labelInfos = labelInfosRaw
        .map((x: any) => {
          const name = String(x?.name ?? x?.labelName ?? x?.label_name ?? x?.title ?? '').trim();
          const rawId = x?.id ?? x?.labelId ?? x?.label_id ?? x?.value ?? '';
          const id = String(rawId ?? '').trim() || name;
          if (!name) return null;
          return { id, name };
        })
        .filter(Boolean) as BroadcastLabelInfo[];
    } else if (labelInfosRaw && typeof labelInfosRaw === 'object') {
      labelInfos = Object.entries(labelInfosRaw).map(([k, v]) => ({ id: String(k).trim(), name: String(v ?? '').trim() }));
    }
    if (labelInfos.length === 0) {
      const labelNamesRaw = raw?.labelNames ?? raw?.labels ?? raw?.tags ?? [];
      const labelNames = Array.isArray(labelNamesRaw)
        ? labelNamesRaw.map((x: any) => String(x ?? '').trim()).filter(Boolean)
        : String(labelNamesRaw ?? '')
            .split(/[，,]/g)
            .map(s => s.trim())
            .filter(Boolean);
      labelInfos = labelNames.map(name => ({ id: name, name }));
    }
    const labelInfoMap = new Map<string, BroadcastLabelInfo>();
    labelInfos.forEach(item => {
      const key = String(item.id || item.name).trim();
      if (!key) return;
      labelInfoMap.set(key, { id: item.id || key, name: item.name });
    });
    labelInfos = Array.from(labelInfoMap.values());
    const expectedCount = Number(raw?.expectedCount ?? raw?.expectCount ?? raw?.estimateCount ?? raw?.targetCount ?? raw?.totalCount ?? 0);
    const successCount = Number(raw?.successCount ?? raw?.succeedCount ?? raw?.deliveredCount ?? raw?.success ?? 0);
    const failedCount = Number(raw?.failedCount ?? raw?.failCount ?? raw?.failureCount ?? raw?.failed ?? 0);
    return {
      id,
      title,
      remark,
      status,
      broadcastTime: resolvedBroadcastTime != null && resolvedBroadcastTime > 0 ? resolvedBroadcastTime : null,
      channel,
      labelInfos,
      expectedCount: Number.isFinite(expectedCount) ? expectedCount : 0,
      successCount: Number.isFinite(successCount) ? successCount : 0,
      failedCount: Number.isFinite(failedCount) ? failedCount : 0
    };
  }

  private mapBroadcastTaskDetail(raw: any, fallbackId: string): BroadcastTaskDetail {
    const id = String(raw?.id ?? raw?.taskId ?? raw?.broadcastId ?? fallbackId ?? '').trim();
    const title = String(raw?.name ?? raw?.title ?? '').trim();
    const remark = String(raw?.remark ?? raw?.description ?? '').trim();
    const channel = String(raw?.channel ?? raw?.platform ?? 'Preset').trim() || 'Preset';
    const labelIdsRaw = raw?.labelIds ?? raw?.labels ?? raw?.labelIdList ?? [];
    const labelIds = Array.isArray(labelIdsRaw)
      ? labelIdsRaw.map((x: any) => String(x ?? '').trim()).filter(Boolean)
      : String(labelIdsRaw ?? '')
          .split(/[，,]/g)
          .map(s => s.trim())
          .filter(Boolean);
    const matchLogicRaw = String(raw?.matchLogic ?? raw?.matchMode ?? raw?.match ?? MatchLogic.Or)
      .trim()
      .toUpperCase();
    const matchLogic = matchLogicRaw === MatchLogic.And ? MatchLogic.And : MatchLogic.Or;
    const broadcastTimeRaw = raw?.broadcastTime ?? raw?.sendTime ?? raw?.time ?? null;
    const broadcastTime = broadcastTimeRaw == null ? null : Number(broadcastTimeRaw || 0);
    const contentRaw = raw?.content ?? raw?.messages ?? raw?.messageList ?? [];
    const content = Array.isArray(contentRaw) ? (contentRaw as any[]) : [];
    return {
      id,
      title,
      remark,
      channel,
      labelIds,
      matchLogic,
      broadcastTime: broadcastTime != null && Number.isFinite(broadcastTime) && broadcastTime > 0 ? broadcastTime : null,
      content
    };
  }

  private mapInboundReasonItem(item: any): LeadReasonOption {
    const id = String(item?.id || item?.sourceId || '').trim();
    const name = String(item?.sourceName ?? item?.title ?? '').trim();
    const sourceType = String(item?.sourceType ?? item?.type ?? '').trim();
    const description = String(item?.description ?? '').trim();
    const bizId = String(item?.businessId ?? '').trim();
    const validityPeriod = item?.validityPeriod ?? item?.expireTime ?? item?.expireAt;
    const expireMs = typeof validityPeriod === 'number' ? validityPeriod : Number(validityPeriod || 0);
    const validMode: 'permanent' | 'dated' = expireMs && expireMs > 0 ? 'dated' : 'permanent';
    const validUntil = validMode === 'dated' ? new Date(expireMs).toISOString() : null;
    const count = Number(item?.inboundCount || 0);
    return { id, name, sourceType, description, bizId, validMode, validUntil, count };
  }
}
